import { ComposeCommand, WithComposeId } from 'interfaces/feed'
import { useMemo, useEffect, useState, useCallback } from 'react'
import { createComposeExecCommandSelector } from 'selectors/feedComposeSelectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'

type CommandsHandlers = Partial<{
  [K in ComposeCommand['type']]: (command: Extract<ComposeCommand, { type: K }>) => void
}>

export function useComposeCommand (withComposeId: WithComposeId, handlers: CommandsHandlers) {
  const composeExecCommandSelector = useMemo(createComposeExecCommandSelector, [])
  const execCommand = useStoreSelector((state) => composeExecCommandSelector(state, withComposeId))

  const [initialCommand] = useState(execCommand)

  useEffect(() => {
    if (!execCommand) {
      return
    }

    // skip initial command, to avoid double execution
    if (execCommand === initialCommand) {
      return
    }

    const cb = handlers[execCommand.type] as (command: ComposeCommand) => void
    if (!cb) {
      return
    }

    cb(execCommand)
  }, [execCommand])
}


export function useSuggestActiveElement<T> (suggests: T[], withComposeId: WithComposeId, handleSubmit: (tag: T) => void) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!suggests[activeIndex]) {
      setActiveIndex(0)
    }
  }, [activeIndex])

  const handleSelect = useCallback(() => {
    const tag = suggests[activeIndex]
    if (!tag) {
      return
    }
    handleSubmit(tag)
  }, [handleSubmit, suggests, activeIndex])

  const handleNextSuggest = useCallback(() => {
    if (activeIndex < suggests.length - 1) {
      setActiveIndex(activeIndex + 1)
    }
  }, [activeIndex, setActiveIndex, suggests])

  const handlePrevSuggest = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1)
    }
  }, [activeIndex, setActiveIndex])

  useComposeCommand(withComposeId, {
    useSuggest: handleSelect,
    nextSuggest: handleNextSuggest,
    prevSuggest: handlePrevSuggest,
  })

  return [activeIndex, setActiveIndex] as [typeof activeIndex, typeof setActiveIndex]
}
